import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const FooterWrapper = styled.footer`
  background: white;
  padding: 4rem 2rem;
  border-top: 1px solid #e2e8f0;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2563eb 0%, #000000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const FooterDescription = styled.p`
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const FooterTitle = styled.h3`
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const FooterLink = styled(Link)`
  color: #64748b;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialLink = styled.a`
  color: #64748b;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
  }
`;

const Copyright = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
`;

function Footer() {
  return (
    <FooterWrapper>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>Revguard</FooterLogo>
          <FooterDescription>
            Helping Shopify stores prevent lost sales with AI-powered testing. 
            Catch issues before they impact your customers.
          </FooterDescription>
          <SocialLinks>
            <SocialLink href="https://twitter.com" target="_blank" aria-label="Twitter">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
              </svg>
            </SocialLink>
            <SocialLink href="https://linkedin.com" target="_blank" aria-label="LinkedIn">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/>
                <circle cx="4" cy="4" r="2"/>
              </svg>
            </SocialLink>
            <SocialLink href="https://github.com" target="_blank" aria-label="GitHub">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </SocialLink>
          </SocialLinks>
        </FooterColumn>
        <FooterColumn>
          <FooterTitle>Company</FooterTitle>
          <FooterLink to="/contact">Contact</FooterLink>
          <FooterLink to="/pricing">Pricing</FooterLink>
          <FooterLink to="/faq">FAQ</FooterLink>
        </FooterColumn>
        <FooterColumn>
          <FooterTitle>Solutions</FooterTitle>
          <FooterLink to="/services/checkout">Checkout Testing</FooterLink>
          <FooterLink to="/services/performance">Performance Testing</FooterLink>
          <FooterLink to="/services/integration">Integration Testing</FooterLink>
          <FooterLink to="/services/security">Security Testing</FooterLink>
          <FooterLink to="/services/mobile">Mobile Testing</FooterLink>
          <FooterLink to="/services/automation">Automation Testing</FooterLink>
        </FooterColumn>
        <FooterColumn>
          <FooterTitle>Legal</FooterTitle>
          <FooterLink to="/privacy">Privacy Policy</FooterLink>
          <FooterLink to="/terms">Terms of Service</FooterLink>
        </FooterColumn>
      </FooterContent>
      <Copyright>
        © {new Date().getFullYear()} Revguard. All rights reserved.
      </Copyright>
    </FooterWrapper>
  );
}

export default Footer;